import type { CartPricing, CouponDiscount } from '../types';
import { shippingOnlyPartnerHardwareValueInCents } from './shippingOnlyPartnerOffer';

/**
 * The free months a GwP free-trial code gives and the value web puts on them ($16 for 1, $50 for 3), read from the
 * validated code so the page can't advertise a different term than the code carries. Null for any other discount.
 */
export function gwpFreeTrialOf(discount: CouponDiscount) {
  if (discount.type !== 'free_trial' || discount.trialUnit !== 'month' || !discount.displayedValueInCents) {
    return null;
  }
  return { months: discount.trialLength, valueInCents: discount.displayedValueInCents };
}

/**
 * A GwP code the redeem page can honor: dollars off the plan, or a partner's Mini free trial it can show. Any other free
 * trial (day-based, or one web puts no value on) would validate with nothing to display, so it's refused.
 */
export function isRedeemableGwpDiscount(discount: CouponDiscount) {
  return (discount.type === 'dollars' && discount.discountAmountInCents > 0) || gwpFreeTrialOf(discount) !== null;
}

/**
 * The bag's partner free trial: the device web says the offer covers, its free months and their value, and the total
 * gifted (the kit + the months: $115 or $149, C$172 or C$219), which the bag shows in place of the plan's own price, like The Pets
 * Table's kit + membership. A self-serve offer's device counts only when the offer gives it away ($99 + $47 = $146).
 * Null when the cart has none.
 */
export function partnerFreeTrialInCart(
  cartPricing: Pick<CartPricing, 'couponDiscount' | 'shippingOnlyPartnerOfferCartItemId' | 'gwpOfferGift' | 'currency'>,
) {
  const trial = cartPricing.couponDiscount ? gwpFreeTrialOf(cartPricing.couponDiscount) : null;
  const covered = cartPricing.shippingOnlyPartnerOfferCartItemId
    ? {
        cartItemId: cartPricing.shippingOnlyPartnerOfferCartItemId,
        deviceValueInCents: shippingOnlyPartnerHardwareValueInCents(cartPricing.currency),
      }
    : cartPricing.gwpOfferGift && {
        cartItemId: cartPricing.gwpOfferGift.cartItemId,
        deviceValueInCents: cartPricing.gwpOfferGift.freeDeviceValueInCents,
      };
  if (!trial || !covered) {
    return null;
  }
  return { ...trial, ...covered, giftInCents: covered.deviceValueInCents + trial.valueInCents };
}
